import {Check, Clock, AlertTriangle} from 'lucide-react';
import {statusTone, TONE_BADGE, TONE_BORDER, TONE_ROW, worstStatus} from './status';
import type {Submission} from '@/api/submissions';
import type {SubmissionStatusGroup} from '@/api/items';

// Shared across the /submission page (Submission.action_status) and Investigations
// (SubmissionStatusGroup.status) — both use the same handful of status strings, so the
// "already submitted" treatment only needs to live in one place. The colours themselves come from
// lib/status, on the design system's status tokens.

// The /submission pages (SingleVideoSubmissionPage, PinterestSubmissionPage) build their own
// synthetic Item for ClassifyDialog - there's no real item_read_models row yet for a fetched-live
// entity, so ItemsController's own submission_status_groups SQL never runs for it. Those pages
// already fetch this exact entity's prior submissions (via useExistingSubmissions, for
// ExistingSubmissionBadge) - this reshapes that same Submission[] into the groups ClassifyDialog's
// hasLiveSubmissionIn needs, so the resubmit-detection check works there too instead of silently
// seeing an always-empty list and letting a same-vertical resubmit 409.
export function groupSubmissionsByStatus(
  submissions: readonly Submission[]
): SubmissionStatusGroup[] {
  const byStatus = new Map<string, {policyViolations: Set<string>; submittedAt: string | null}>();
  for (const s of submissions) {
    const group = byStatus.get(s.action_status) ?? {
      policyViolations: new Set<string>(),
      submittedAt: null
    };
    group.policyViolations.add(s.policy_violation);
    // Matches ItemsController's own rule: only a historic row (whose export may carry a real send
    // date) ever populates submitted_at - a live row contributes null, same as the server's
    // NULL::timestamptz branch.
    if (s.is_historic) {
      group.submittedAt = s.delivered_at ?? s.created_at;
    }

    byStatus.set(s.action_status, group);
  }

  return [...byStatus.entries()].map(([status, group]) => ({
    status,
    policy_violations: [...group.policyViolations],
    submitted_at: group.submittedAt
  }));
}

export function worstStatusBadgeClassName(statuses: readonly string[]): string {
  return TONE_BADGE[statusTone(worstStatus(statuses))];
}

// Same priority, but as a border so the whole card reads in the status colour at a glance — not
// just a small corner badge.
export function worstStatusBorderClassName(statuses: readonly string[]): string {
  return TONE_BORDER[statusTone(worstStatus(statuses))];
}

// Same priority again, but as a left-edge strip for table rows — a ring reads oddly inside a
// bordered table.
export function worstStatusRowBorderClassName(statuses: readonly string[]): string {
  return TONE_ROW[statusTone(worstStatus(statuses))];
}

// Same icons the Investigations page already uses for these statuses.
export function worstStatusIcon(statuses: readonly string[]) {
  const status = worstStatus(statuses);
  if (status === 'sent to client') {
    return Check;
  }

  if (status === 'in hopper') {
    return Clock;
  }

  return AlertTriangle;
}
